import { useEffect, useMemo } from 'react';
import { createZodFormResolver, SubmitHandler, useFormControls } from '@mews/framework';
import { RoleGetResult, RoleResult } from '@mews-commander/data-access';
import { isDuplicateRoleNameError } from '../../utils/isDuplicateRoleNameError';
import { EditRoleFormValues, editRoleSchema } from '../schema';
import { mapPrivilegesToRowSelection } from '../PrivilegesTable';
import { EditRoleMutationParams, useEditRole } from './queries';

export enum EditRoleFormFieldNames {
    Name = 'name',
    Privileges = 'privileges',
}

const resolver = createZodFormResolver<EditRoleFormValues>(editRoleSchema);

export const useEditRoleForm = ({
    role,
    onSuccess,
    onError,
}: {
    onSuccess?: (data: RoleResult, variables: EditRoleMutationParams, context?: unknown) => void;
    onError?: () => void;
    role: RoleGetResult;
}) => {
    const editRole = useEditRole();

    const defaultValues = useMemo(
        () => ({
            name: role.name,
            privileges: role.privileges ? mapPrivilegesToRowSelection(role.privileges) : {},
        }),
        [role]
    );

    const handleSubmit: SubmitHandler<EditRoleFormValues> = (values, { dirtyFields }) =>
        editRole.mutate(
            {
                oldName: role.name,
                role: {
                    id: role.id,
                    ...(dirtyFields.name && { name: { value: values.name } }),
                    ...(dirtyFields.privileges && { privileges: { value: values.privileges } }),
                },
            },
            {
                onSuccess,
                onError: (error) => {
                    if (isDuplicateRoleNameError(error)) {
                        setError(EditRoleFormFieldNames.Name, { type: 'validate', message: error.message });
                        onError?.();
                    }
                },
            }
        );

    const {
        setError,
        reset: internalReset,
        ...otherMethods
    } = useFormControls<EditRoleFormValues>({
        resolver,
        defaultValues,
        onSubmit: handleSubmit,
        shouldFocusError: true,
    });

    // Since the form is not being mounted/unmounted, we need to reset the form when the default values change
    useEffect(() => {
        internalReset(defaultValues);
    }, [internalReset, defaultValues]);

    return { ...otherMethods, reset: () => internalReset(defaultValues), isLoading: editRole.isPending };
};
